import { useState } from "react";
import { Card, SectionTitle, InfoIcon } from "./ui";
import type { Variant } from "../data/data";
import type { HStudent } from "../data/hierarchy";

type UsageMetric = "hours" | "lessons" | "teachers";
type ViewDim = "site" | "grade" | "teacher" | "category";

const VIEW_OPTIONS: { key: ViewDim; label: string }[] = [
  { key: "site", label: "Site" },
  { key: "grade", label: "Grade" },
  { key: "teacher", label: "Teacher" },
  { key: "category", label: "Category" },
];

const TITLES: Record<UsageMetric, string> = {
  hours: "Tutoring hours used",
  lessons: "Lessons completed",
  teachers: "Active teachers",
};

const INFO: Record<UsageMetric, string> = {
  hours:
    "Total 1:1 AI-tutor hours consumed against the hours contracted for the year.",
  lessons:
    "Total lessons students have finished, including the assessed check for understanding.",
  teachers:
    "Share of teachers in the current selection actively using the platform in the period.",
};

const fmt = (n: number) => n.toLocaleString("en-US");

function groupValue(student: HStudent, dim: ViewDim): string {
  if (dim === "site") return student.site;
  if (dim === "grade") return `Grade ${student.grade}`;
  if (dim === "teacher") return student.teacher;
  return student.category;
}

function groupStudents(
  students: HStudent[],
  dim: ViewDim,
): { label: string; students: HStudent[] }[] {
  const grouped = new Map<string, HStudent[]>();
  students.forEach((student) => {
    const label = groupValue(student, dim);
    grouped.set(label, [...(grouped.get(label) || []), student]);
  });
  return Array.from(grouped, ([label, members]) => ({
    label,
    students: members,
  }));
}

function ViewBy({
  value,
  onChange,
}: {
  value: ViewDim;
  onChange: (value: ViewDim) => void;
}) {
  return (
    <div className="inline-flex rounded-[6px] border border-[#DED8CC] bg-[#F3EDE2] p-[3px]">
      {VIEW_OPTIONS.map((option) => {
        const active = option.key === value;
        return (
          <button
            key={option.key}
            onClick={() => onChange(option.key)}
            className={`cursor-pointer rounded-[4px] border-none px-3 py-[5px] text-[12.5px] font-bold ${
              active
                ? "bg-[#FBF8F1] text-[#28543D] shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                : "bg-transparent text-[#687368]"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function HorizontalBars({
  rows,
  suffix,
}: {
  rows: { label: string; value: number; display: string }[];
  suffix: string;
}) {
  const max = Math.max(1, ...rows.map((row) => row.value));
  return (
    <div className="flex flex-col">
      {rows.map((row) => (
        <div
          key={row.label}
          className="grid items-center gap-3.5 border-t border-[#DED8CC] py-3"
          style={{ gridTemplateColumns: "1.25fr 1fr 90px" }}
        >
          <span className="truncate text-[13.5px] font-bold text-[#294B39]">
            {row.label}
          </span>
          <div className="h-[9px] overflow-hidden rounded-full bg-[#28543D]/5">
            <div
              className="h-full rounded-full bg-[#6E9A7B]"
              style={{ width: `${(row.value / max) * 100}%` }}
            />
          </div>
          <span className="text-right text-[13.5px] font-bold text-[#294B39]">
            {row.display} {suffix}
          </span>
        </div>
      ))}
    </div>
  );
}

function Trend({ values, suffix }: { values: number[]; suffix: string }) {
  const labels = ["Aug", "Sep", "Oct", "Nov", "Dec", "Jan"];
  const max = Math.max(1, ...values);
  return (
    <div>
      <div className="flex h-[210px] items-end gap-3.5">
        {values.map((value, index) => (
          <div
            key={labels[index]}
            className="flex h-full flex-1 flex-col items-center justify-end gap-[7px]"
          >
            <span className="text-xs font-bold text-[#294B39]">
              {fmt(value)}
              {suffix}
            </span>
            <div
              className="w-full rounded-t-md bg-[#6E9A7B]"
              style={{ height: `${Math.max(8, (value / max) * 165)}px` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-3.5">
        {labels.map((label) => (
          <span
            key={label}
            className="flex-1 text-center text-xs text-[#687368]"
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function UsageMetricDetails({
  metric,
  variant,
  students,
}: {
  metric: UsageMetric;
  variant: Variant;
  students: HStudent[];
}) {
  const [view, setView] = useState<ViewDim>("site");
  const groups = groupStudents(students, view);
  const totalHours = variant.engagement.tutoringHoursNum;
  const totalLessons = variant.lessons.completed;
  const teacherActivity = new Map(
    variant.teachers.map((teacher) => [teacher.name, teacher.activity]),
  );
  const rawTotal = Math.max(
    1,
    students.reduce((sum, student) => sum + student.avgHours, 0),
  );

  const rows = groups
    .map((group) => {
      const raw = group.students.reduce(
        (sum, student) => sum + student.avgHours,
        0,
      );
      if (metric === "hours") {
        const value = Math.round((raw / rawTotal) * totalHours);
        return { label: group.label, value, display: fmt(value) };
      }
      if (metric === "lessons") {
        const value = Math.round((raw / rawTotal) * totalLessons);
        return { label: group.label, value, display: fmt(value) };
      }
      const teachers = Array.from(
        new Set(group.students.map((student) => student.teacher)),
      );
      const active = teachers.filter(
        (teacher) => (teacherActivity.get(teacher) ?? 0) >= 60,
      ).length;
      const value =
        teachers.length > 0 ? Math.round((active / teachers.length) * 100) : 0;
      return { label: group.label, value, display: `${value}` };
    })
    .sort((a, b) => b.value - a.value);

  const trendBase =
    metric === "hours"
      ? totalHours
      : metric === "lessons"
        ? totalLessons
        : Math.round(
            (variant.teachers.filter((teacher) => teacher.activity >= 60)
              .length /
              Math.max(1, variant.teachers.length)) *
              100,
          );
  const trend =
    metric === "teachers"
      ? [
          trendBase - 8,
          trendBase - 6,
          trendBase - 5,
          trendBase - 3,
          trendBase - 1,
          trendBase,
        ].map((value) => Math.max(0, value))
      : [0.11, 0.13, 0.15, 0.17, 0.2, 0.24].map((share) =>
          Math.round(trendBase * share),
        );
  const unit = metric === "hours" ? "hrs" : metric === "lessons" ? "" : "%";

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-1.5">
          <SectionTitle title={TITLES[metric]} />
          <InfoIcon text={INFO[metric]} />
        </div>
        <ViewBy value={view} onChange={setView} />
      </div>

      <div className="grid grid-cols-[1.1fr_0.9fr] gap-4">
        <Card className="flex flex-col gap-3.5">
          <span className="text-[15px] font-bold text-[#294B39]">
            {TITLES[metric]} by{" "}
            {VIEW_OPTIONS.find(
              (option) => option.key === view,
            )?.label.toLowerCase()}
          </span>
          <HorizontalBars rows={rows} suffix={unit} />
        </Card>
        <Card className="flex flex-col gap-3.5">
          <span className="text-[15px] font-bold text-[#294B39]">
            {TITLES[metric]} trend
          </span>
          <Trend values={trend} suffix={metric === "teachers" ? "%" : ""} />
        </Card>
      </div>
    </section>
  );
}
